import { useState, useCallback } from 'react';
import { opensearchApi, OpenSearchSearchResponse, OpenSearchSemanticSearchParams, OpenSearchHybridSearchParams, OpenSearchNaturalLanguageParams, ParsedQueryResponse } from '@/api/opensearch_results';

export interface UseOpenSearchReturn {
  // State
  results: OpenSearchSearchResponse | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  semanticSearch: (params: OpenSearchSemanticSearchParams) => Promise<void>;
  hybridSearch: (params: OpenSearchHybridSearchParams) => Promise<void>;
  naturalLanguageSearch: (params: OpenSearchNaturalLanguageParams) => Promise<void>;
  parseQuery: (query: string) => Promise<ParsedQueryResponse>;
  clearResults: () => void;
  clearError: () => void;
  
  // Health check
  isHealthy: boolean;
  checkHealth: () => Promise<void>;
}

export const useOpenSearch = (): UseOpenSearchReturn => {
  const [results, setResults] = useState<OpenSearchSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isHealthy, setIsHealthy] = useState(false);

  const semanticSearch = useCallback(async (params: OpenSearchSemanticSearchParams) => {
    setLoading(true);
    setError(null);
    
    try {
      const searchResults = await opensearchApi.semanticSearch(params);
      setResults(searchResults);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Semantic search failed';
      setError(errorMessage);
      console.error('Semantic search error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const hybridSearch = useCallback(async (params: OpenSearchHybridSearchParams) => {
    setLoading(true);
    setError(null);
    
    try {
      const searchResults = await opensearchApi.hybridSearch(params);
      setResults(searchResults);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hybrid search failed';
      setError(errorMessage);
      console.error('Hybrid search error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const naturalLanguageSearch = useCallback(async (params: OpenSearchNaturalLanguageParams) => {
    setLoading(true);
    setError(null);
    
    try {
      const searchResults = await opensearchApi.naturalLanguageSearch(params);
      setResults(searchResults);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Natural language search failed';
      setError(errorMessage);
      console.error('Natural language search error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const parseQuery = useCallback(async (query: string): Promise<ParsedQueryResponse> => {
    try {
      const parsedResult = await opensearchApi.parseQuery(query);
      return parsedResult;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Query parsing failed';
      setError(errorMessage);
      console.error('Query parsing error:', err);
      throw err;
    }
  }, []);

  const clearResults = useCallback(() => {
    setResults(null);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const checkHealth = useCallback(async () => {
    try {
      const healthy = await opensearchApi.healthCheck();
      setIsHealthy(healthy);
    } catch (err) {
      setIsHealthy(false);
      console.error('Health check failed:', err);
    }
  }, []);

  return {
    // State
    results,
    loading,
    error,
    isHealthy,
    
    // Actions
    semanticSearch,
    hybridSearch,
    naturalLanguageSearch,
    parseQuery,
    clearResults,
    clearError,
    checkHealth,
  };
};

export default useOpenSearch;
